import { View, TouchableOpacity, Text } from "react-native";
import { Image } from "expo-image";
import { UploadArea } from "../molecules/UploadArea";
import { useRef } from "react";
import { RemoveImageButton } from "ui/atoms/button/RemoveImageButton";
import { AlertCircleIcon, Icon } from "ui/gluestack/icon";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { FieldError } from "react-hook-form";
import * as mime from "react-native-mime-types";
import { DocumentPickerAsset } from "expo-document-picker";

interface Props {
  value?: DocumentPickerAsset;
  onChange: (file: DocumentPickerAsset | undefined) => void;
  onRemove?: () => void;
  allowedExtensions: string[];
  errorMessage?: FieldError;
}

export function ImageUpload({
  value,
  onChange,
  onRemove,
  allowedExtensions,
  errorMessage,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showErrorToast } = useDisplayToast();
  const handleImageChange = (files: DocumentPickerAsset[]) => {
    const file = files?.[0];
    if (file) {
      const mimeType = mime.lookup(file.name);

      if (!mimeType || !allowedExtensions.includes(mimeType)) {
        showErrorToast("Invalid file extension", {
          title: "File Upload Error",
        });
        return;
      }
      onChange(file);
    } else {
      onChange(undefined);
    }
  };

  const handleRemoveImage = () => {
    onRemove?.();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <View>
      <TouchableOpacity>
        <View className="w-36 h-36 bg-secondary-0 justify-center items-center rounded-full">
          <UploadArea
            onChange={handleImageChange}
            inputRef={fileInputRef}
            allowedExtensions={allowedExtensions}
          />
          {value?.uri ? (
            <Image
              source={{ uri: value.uri }}
              className="w-36 h-36 rounded-full z-10"
            />
          ) : null}
        </View>

        {value?.uri ? (
          <RemoveImageButton onRemoveImage={handleRemoveImage} />
        ) : null}
      </TouchableOpacity>
      {errorMessage ? (
        <View className="flex-row gap-x-1 items-center">
          <Icon as={AlertCircleIcon} className="text-error-500" size="sm" />
          <Text className="text-error-500 text-sm">
            {errorMessage?.message}
          </Text>
        </View>
      ) : null}
    </View>
  );
}
